import { groupBy, mapValues } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';
import { produce } from 'immer';

import { Insertion, insertionsEqual, LayoutModel, ModelCategory } from '@paragon/contract-library';
import { assertExhaustiveSwitch, ReadonlyRecord } from '@paragon/utilities';

import { LayoutModelsLibraryState } from '../store';
import { getModifiedModels, hasModifiedInsertions } from '../store/changeTracker';
import { applyPatches } from '../store/changeTracker/patchTracker';

const getChangeTracker = (state: LayoutModelsLibraryState) => state.changeTracker;

export const changesExist = createSelector(
  getChangeTracker,
  (changes) =>
    getModifiedModels(changes.modifiedModels).length > 0 || hasModifiedInsertions(changes.modifiedInsertions),
);

export const getLayoutModelsWithPendingChanges = createSelector(
  (state: LayoutModelsLibraryState) => state.layoutModels,
  getChangeTracker,
  (layoutModels, changeTracker) => {
    const updatedLayoutModels = mapValues(layoutModels, (model) => {
      switch (model.category) {
        case ModelCategory.ComponentDesign:
          return model; // component designs are not editable by the layout models library

        case ModelCategory.Project:
        case ModelCategory.IntermediateModel:
          const change = changeTracker.modifiedModels[model.guid];

          if (!change) return model;
          if (change.type === 'created') return change.model;

          return applyPatches(model, change.patches);

        case ModelCategory.ComponentDesignData:
          throw new Error('Component design data should not be stored in the layoutModels reducer.');

        default:
          assertExhaustiveSwitch(model.category);
      }
    });

    const newlyCreatedLayoutModels: Record<string, LayoutModel> = {};
    for (const change of Object.values(changeTracker.modifiedModels)) {
      if (change.type === 'created') {
        newlyCreatedLayoutModels[change.model.guid] = change.model;
      }
    }

    return { ...updatedLayoutModels, ...newlyCreatedLayoutModels };
  },
);

export const getInsertionsWithPendingChanges = createSelector(
  (state: LayoutModelsLibraryState) => state.insertions,
  getChangeTracker,
  (insertions, changeTracker) => {
    const updatedInsertions = produce(insertions, (draft) => {
      // We splice instead of just filtering because there may be multiple "identical" insertions, of which only a few
      //   were removed.
      for (const removedInsertion of changeTracker.modifiedInsertions.removed) {
        const index = draft.findIndex((existingInsertion) => insertionsEqual(existingInsertion, removedInsertion));
        if (index === -1) {
          continue;
        }

        draft.splice(index, 1);
      }
    });

    return updatedInsertions.concat(changeTracker.modifiedInsertions.created);
  },
);

export const getInsertionsWithoutPendingChangesGroupedByObjectGuid = createSelector(
  (state: LayoutModelsLibraryState) => state.insertions,
  (insertions): ReadonlyRecord<string, readonly Insertion[] | undefined> =>
    groupBy(insertions, (insertion) => insertion.objectGuid),
);

export const getInsertionsWithPendingChangesGroupedByObjectGuid = createSelector(
  getInsertionsWithPendingChanges,
  (insertions): ReadonlyRecord<string, readonly Insertion[] | undefined> =>
    groupBy(insertions, (insertion) => insertion.objectGuid),
);

export const getInsertionsWithPendingChangesGroupedByParentGuid = createSelector(
  getInsertionsWithPendingChanges,
  (insertions): ReadonlyRecord<string, readonly Insertion[] | undefined> =>
    groupBy(insertions, (insertion) => insertion.parentGuid),
);
